'use client'

import { useMemo } from 'react'

import { useOptimizedRecords } from '@/shared/hooks/useOptimizedQuery'

import {
  isDisplayableRewardRequest,
  resolveRewardRequestState,
  type RewardRequestVisibleState
} from '../lib/rewardRequestStatus'
import type { RewardRequest } from '../types/reward'

// O backend filtra via `params[:q]` (ransack) e `reward_id`/`status` não são
// atributos ransackáveis diretos de reward_requests; por isso buscamos os
// resgates do aluno (escopo garantido por `current_user`) e filtramos/indexamos
// no cliente. Uma única requisição abastece histórico, indicadores e modal.
//
// Usa `useOptimizedRecords` (não `useRecords`): este constrói a URL como
// `path?query`, enquanto o `useRecords` + defaultQueryFn interpretava a
// query-string como um id (`/user/reward_requests/pagination[per_page]=200`),
// quebrando o fetch e retornando lista vazia.
const PER_PAGE = 200

type UseUserRewardRequestsReturn = {
  records: RewardRequest[]
  isLoading: boolean
  refetch: () => void
}

export function useUserRewardRequests(): UseUserRewardRequestsReturn {
  const { records, isLoading, refetch } = useOptimizedRecords<RewardRequest>({
    path: '/user/reward_requests',
    params: { pagination: { per_page: PER_PAGE } }
  })

  return { records, isLoading, refetch }
}

type UseRewardRequestsHistoryReturn = {
  requests: RewardRequest[]
  isLoading: boolean
  isEmpty: boolean
  refetch: () => void
}

/**
 * Histórico do aluno: resgates em processamento ou bem-sucedidos
 * (aguardando aprovação, aguardando retirada, resgatado), já ordenados por
 * data desc pelo backend. Estados terminais negativos são ocultados.
 */
export function useRewardRequestsHistory(): UseRewardRequestsHistoryReturn {
  const { records, isLoading, refetch } = useUserRewardRequests()

  const requests = useMemo(
    () => records.filter(isDisplayableRewardRequest),
    [records]
  )

  return {
    requests,
    isLoading,
    isEmpty: !isLoading && requests.length === 0,
    refetch
  }
}

// Precedência quando um mesmo prêmio tem múltiplos resgates: mostra o estado
// mais acionável primeiro.
const STATE_PRIORITY: Record<RewardRequestVisibleState, number> = {
  awaiting_pickup: 3,
  pending_approval: 2,
  redeemed: 1
}

type RedemptionByReward = Record<number, RewardRequestVisibleState>

/**
 * Indexa os resgates exibíveis do aluno por `reward_id`, abastecendo os
 * indicadores da listagem com uma única requisição.
 */
export function useRewardRedemptionsByReward(): {
  stateByReward: RedemptionByReward
  isLoading: boolean
} {
  const { records, isLoading } = useUserRewardRequests()

  const stateByReward = useMemo<RedemptionByReward>(() => {
    const map: RedemptionByReward = {}

    for (const request of records) {
      const rewardId = request.reward_item?.reward?.id ?? request.reward_id
      if (!rewardId) continue

      const state = resolveRewardRequestState(request)
      if (state === 'none') continue

      const current = map[rewardId]
      if (!current || STATE_PRIORITY[state] > STATE_PRIORITY[current]) {
        map[rewardId] = state
      }
    }

    return map
  }, [records])

  return { stateByReward, isLoading }
}
